import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../supabase";
import { IoBookmarkOutline, IoBookmark } from "react-icons/io5";
import useSnackbarNotification from "../hooks/useSnackbarNotification";
import ShareDropdown from "../components/ShareDropdown";
import { fetchSponsoredEmployerIds } from "../services/listingPurchases";

// Map categories to CSS classes to match the company search page colors
const getCategoryClass = (category) => {
    if (!category) return "";
    
    const categoryMap = {
        "Business/Consulting": "business-consulting",
        "Clinical Medicine": "clinical-medicine",
        "Clinical Research": "clinical-research", 
        "Entrepreneurship": "entrepreneurship",
        "HealthTech": "health-tech",
        "Hospital/Clinic Management": "hospital-clinic-management",
        "Insurance": "insurance",
        "Marketing/Sales": "marketing-sales",
        "Medical Communication": "medical-communication",
        "Medical Education": "medical-education",
        "Pharmaceuticals": "pharmaceuticals",
        "Policy/Legal/Ethics": "policy-legal-ethics",
        "Project Management": "project-management",
        "Wellness/lifestyle": "wellness-lifestyle"
    };
    
    return categoryMap[category] || "business-consulting";
};

// Use the same categories as company search page
const CATEGORY_OPTIONS = [
    "Business/Consulting",
    "Clinical Medicine", 
    "Clinical Research",
    "Entrepreneurship",
    "HealthTech",
    "Hospital/Clinic Management",
    "Insurance",
    "Marketing/Sales",
    "Medical Communication",
    "Medical Education", 
    "Pharmaceuticals",
    "Policy/Legal/Ethics",
    "Project Management",
    "Wellness/lifestyle"
];

const FilterChip = ({ label, active, onClick }) => {
    const categoryClass = getCategoryClass(label);
    return (
        <button
            className={`category-pill ${categoryClass} ${active ? 'active' : ''}`}
            onClick={onClick}
        >
            {label}
        </button>
    );
};

const RecommendedMatches = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { showSuccess, showError } = useSnackbarNotification();
    
    const [jobMatches, setJobMatches] = useState([]);
    const [filteredMatches, setFilteredMatches] = useState([]);
    const [shareDropdown, setShareDropdown] = useState({ open: false, job: null });
    const [categoryFilter, setCategoryFilter] = useState("");
    const [sortBy, setSortBy] = useState("match");
    const [bookmarkedJobs, setBookmarkedJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showSortDropdown, setShowSortDropdown] = useState(false);
    const [dismissedJobMatches, setDismissedJobMatches] = useState([]);
    const [availableCategories, setAvailableCategories] = useState([]);

    useEffect(() => {
        if (user) {
            // Load dismissed job matches from user metadata so we can keep
            // Recommended Matches in sync with Top Job Matches on the dashboard
            const dismissed = user.user_metadata?.dismissed_job_matches || [];
            setDismissedJobMatches(dismissed);
            fetchJobMatches();
            fetchBookmarkedJobs();
        }
    }, [user]);

    useEffect(() => {
        filterAndSortJobs();
    }, [jobMatches, categoryFilter, sortBy]);

    const fetchBookmarkedJobs = async () => {
        if (!user) return;
        
        try {
            const { data, error } = await supabase
                .from('applications')
                .select('job_listing_id')
                .eq('user_id', user.id)
                .eq('status', 'bookmarked');
                
            if (error) throw error;
            
            setBookmarkedJobs(data.map(item => item.job_listing_id));
        } catch (error) {
            console.error('Error fetching bookmarked jobs:', error);
        }
    };

    const fetchJobMatches = async () => {
        try {
            setLoading(true);
            
            // Get employee profile data for matching
            const employeeProfile = {
                country: user.user_metadata?.country || user.user_metadata?.my_profile?.profile_personal_info?.country || null,
                provinceState: user.user_metadata?.province_state || user.user_metadata?.my_profile?.profile_personal_info?.provinceState || null,
                employmentType: user.user_metadata?.employment_status || null,
                trainingLevel: user.user_metadata?.training_level || null,
                // Use profile_role.categories (What category best fits what you're looking for? Select all that apply)
                category: user.user_metadata?.my_profile?.profile_role?.categories || 
                         user.user_metadata?.profile_categories || 
                         null
            };

            const { data: jobListings, error } = await supabase
                .from('internal_job_listings')
                .select('*')
                .eq('status', 'published')
                .order('created_at', { ascending: false });

            const { data: externalJobListings, error: externalError } = await supabase
                .from('jobs')
                .select('*')
                .eq('live', true)
                .order('date_added', { ascending: false });

            if (externalError) throw externalError;

            if (error) throw error;

            let sponsoredEmployerIds = new Set();
            try {
                const sponsorIds = await fetchSponsoredEmployerIds();
                sponsoredEmployerIds = new Set(sponsorIds);
            } catch (err) {
                console.error("Error fetching sponsored employers:", err);
            }

            const now = new Date();
            const thirtyDaysAgo = new Date(now);
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

            let freshJobListings = (jobListings || []).filter(job => {
                if (!job?.created_at) return false;
                const createdAt = new Date(job.created_at);
                return createdAt >= thirtyDaysAgo;
            });

            freshJobListings = [...freshJobListings, ...externalJobListings];

            console.log('freshJobListings', freshJobListings);

            const matches = freshJobListings.map(job => {
                let score = 0;
                // Use fixed weights (out of 100). This prevents any category (e.g. Location)
                // from becoming a larger share just because other checks weren't counted.
                const totalChecks = 100;
                const matchedCategories = [];

                // 1. Location match (25% weight)
                // More lenient: 12.5% for country match, additional 12.5% if province also matches
                if (employeeProfile.country && (job.country || job.location)) {
                    let locationScore = 0;
                    if (job.country) {
                        const employeeCountryLower = employeeProfile.country.toLowerCase().trim();
                        const jobCountryLower = job.country.toLowerCase().trim();
                        const countryMatch = employeeCountryLower === jobCountryLower ||
                                           jobCountryLower.includes(employeeCountryLower) ||
                                           employeeCountryLower.includes(jobCountryLower);
                        
                        let provinceMatch = false;
                        if (employeeProfile.provinceState && job.province) {
                            const employeeProvinceLower = employeeProfile.provinceState.toLowerCase().trim();
                            const jobProvinceLower = job.province.toLowerCase().trim();
                            provinceMatch = employeeProvinceLower === jobProvinceLower ||
                                           jobProvinceLower.includes(employeeProvinceLower) ||
                                           employeeProvinceLower.includes(jobProvinceLower);
                        } else if (!employeeProfile.provinceState && !job.province) {
                            // If neither has province, give full province score
                            provinceMatch = true;
                        }
                        
                        // Calculate location score: 12.5% for country, 12.5% for province (if applicable)
                        if (countryMatch) {
                            locationScore = 12.5; // Country match gives 12.5%
                            if (provinceMatch) {
                                locationScore = 25; // Both match gives full 25%
                            }
                        }
                    } else if (job.location) {
                        // Fallback to legacy location field matching - gives full 25%
                        const employeeCountryLower = employeeProfile.country.toLowerCase().trim();
                        const jobLocationLower = job.location.toLowerCase().trim();
                        const locationMatch = jobLocationLower.includes(employeeCountryLower) ||
                                           employeeCountryLower.includes(jobLocationLower);
                        if (locationMatch) {
                            locationScore = 25;
                        }
                    }
                    
                    if (locationScore > 0) {
                        score += Math.min(25, locationScore);
                        matchedCategories.push("Location");
                    }
                }

                //1b province match (25% weight) for scraped jobs
                let provinceScore = 0;
                if (
                    employeeProfile.provinceState &&
                    job.province &&
                    !job.country &&
                    !employeeProfile.country
                ) {
                    const employeeProvinceLower = employeeProfile.provinceState.toLowerCase().trim();
                    const jobProvinceLower = job.province.toLowerCase().trim();
                    const provinceMatch = employeeProvinceLower === jobProvinceLower ||
                      jobProvinceLower.includes(employeeProvinceLower) ||
                      employeeProvinceLower.includes(jobProvinceLower);
                    if (provinceMatch) {
                      provinceScore = 12.5;
                    }
                    if (provinceScore > 0) {
                      // Province-only hint should never make location exceed 25
                      score += Math.min(12.5, provinceScore);
                      matchedCategories.push("Location");
                    }
                }
                

                // 2. Employment type match (25% weight)
                if (job.employment_type && employeeProfile.employmentType) {
                    const jobType = job.employment_type.toLowerCase();
                    const employeeType = employeeProfile.employmentType.toLowerCase();
                    const employmentMatch = jobType === employeeType || 
                        (jobType.includes('full') && employeeType.includes('full')) ||
                        (jobType.includes('part') && employeeType.includes('part'));
                    
                    if (employmentMatch) {
                        score += 25;
                        matchedCategories.push("Employment type");
                    }
                }

                //2b employment type match (25% weight) for scraped jobs
                let employmentTypeScore = 0;
          if (job.fulltime_parttime && employeeProfile.employmentType && !job.country) {
            const jobType = job.fulltime_parttime.toLowerCase();
            const employmentMatch = jobType.includes(employeeProfile.employmentType.toLowerCase());
            if (employmentMatch) {
              employmentTypeScore = 25;
              if (employmentTypeScore > 0) {    
                score += employmentTypeScore;
                matchedCategories.push("Employment type");
              }
            }
          }
         

                // 3. Experience level match (25% weight) - Employee training level vs Job experience level
                // Map employee training_level to job experience_level
                const mapTrainingToExperience = (trainingLevel) => {
                    if (!trainingLevel) return [];
                    const level = trainingLevel.toLowerCase();
                    // Map employee training levels to compatible job experience levels
                    switch(level) {
                        case 'student':
                            return ['medical-student'];
                        case 'junior':
                            return ['junior-doctor', 'medical-officer'];
                        case 'senior':
                            return ['registrar', 'fellow', 'specialist', 'consultant'];
                        case 'other':
                            // "other" can match any level
                            return ['medical-student', 'junior-doctor', 'medical-officer', 'registrar', 'fellow', 'specialist', 'consultant'];
                        default:
                            return [];
                    }
                };
                
                // Only check experience level if both have data
                if (job.experience_level && employeeProfile.trainingLevel) {
                    const jobExperienceLevel = job.experience_level.toLowerCase();
                    const compatibleLevels = mapTrainingToExperience(employeeProfile.trainingLevel);
                    const experienceMatch = compatibleLevels.includes(jobExperienceLevel);
                    
                    if (experienceMatch) {
                        score += 25;
                        matchedCategories.push("Experience");
                    }
                }

                //3b experience level match (25% weight) for scraped jobs
                let experienceScore = 0;
          if (job.experience && employeeProfile.trainingLevel && !job.country) {
            const jobExperienceLevel = job.experience.toLowerCase();
            const experienceMatch = jobExperienceLevel.includes(employeeProfile.trainingLevel.toLowerCase());
            if (experienceMatch) {
              score += 25;
              experienceScore = 25; 
              matchedCategories.push("Experience");
            }
          }
          

                // 4. Category/Roles match (25% weight)
                if (job.specialty && employeeProfile.category) {
                    const jobCategory = job.specialty.toLowerCase();
                    let categoryMatch = false;
                    
                    if (Array.isArray(employeeProfile.category)) {
                        categoryMatch = employeeProfile.category.some(cat => {
                            if (!cat) return false;
                            const catStr = typeof cat === 'string' ? cat.toLowerCase() : String(cat).toLowerCase();
                            return catStr === jobCategory || 
                                   catStr.includes(jobCategory) || 
                                   jobCategory.includes(catStr);
                        });
                    } else if (typeof employeeProfile.category === 'string') {
                        categoryMatch = employeeProfile.category.toLowerCase() === jobCategory ||
                            employeeProfile.category.toLowerCase().includes(jobCategory) ||
                            jobCategory.includes(employeeProfile.category.toLowerCase());
                    }
                    
                    if (categoryMatch) {
                        score += 25;
                        matchedCategories.push("Specialty");
                    }
                }

                //4b category/roles match (25% weight) for scraped jobs
                let categoryScore = 0;
          if (job.category && employeeProfile.category && !job.country) {
            const jobCategory = job.category.toLowerCase();
            const categoryMatch = employeeProfile.category.includes(jobCategory);
            if (categoryMatch) {
              score += 25;
              categoryScore = 25;
              matchedCategories.push("Specialty");
            }
          }
          
                const uniqueMatchedCategories = [...new Set(matchedCategories)];

                // Calculate match percentage accurately
                let matchPercentage = 0;
                if (
                    employeeProfile.country ||
                    employeeProfile.employmentType ||
                    employeeProfile.trainingLevel ||
                    employeeProfile.category
                ) {
                    const rawPercentage = (score / totalChecks) * 100;
                    matchPercentage = Math.round(rawPercentage);
                    matchPercentage = Math.max(0, Math.min(100, matchPercentage));
                } else {
                    // If user has no profile data, assign a baseline match percentage
                    // This ensures jobs still show up even without profile matching
                    matchPercentage = 10; // Baseline 10% match
                }
                const postedDays = Math.max(
                    1,
                    Math.ceil((new Date() - new Date(job.created_at)) / (1000 * 60 * 60 * 24))
                );
                const isSponsored = sponsoredEmployerIds.has(job.employer_id);
                const dismissedIds = user.user_metadata?.dismissed_job_matches || [];
                
                return {
                    id: job.id,
                    matchPercentage,
                    matchedCategories: uniqueMatchedCategories,
                    title: job.title,
                    company: job.company_name,
                    location: job.location || `${job.province || ''} ${job.country || ''}`.trim(),
                    postedDays,
                    status: "available",
                    created_at: job.created_at,
                    specialty: job.specialty || job.category,
                    isSponsored,
                    isDismissed: dismissedIds.includes(job.id),
                    application_url: job.application_url || null 
                };
            }).filter(job => job.matchPercentage > 0 && !job.isDismissed)
              .sort((a, b) => {
                if (a.isSponsored !== b.isSponsored) {
                    return a.isSponsored ? -1 : 1;
                }
                if (b.matchPercentage !== a.matchPercentage) {
                    return b.matchPercentage - a.matchPercentage;
                }
                return new Date(b.created_at) - new Date(a.created_at);
            });

            // Build available categories from the matched jobs so that we only
            // render category filter options for categories that actually have
            // at least one recommended match.
            const categoriesFromMatches = [...new Set(matches.map(job => job.specialty))].filter(Boolean);
            const categoriesWithMatches = CATEGORY_OPTIONS.filter(cat =>
                categoriesFromMatches.includes(cat)
            );

            setAvailableCategories(categoriesWithMatches);
            setJobMatches(matches);
        } catch (error) {
            console.error("Error fetching job matches:", error);
        } finally {
            setLoading(false);
        }
    };

    const filterAndSortJobs = () => {
        let filtered = [...jobMatches];

        // Filter by category (if category filter is applied)
        if (categoryFilter) {
            filtered = filtered.filter(job => job.specialty === categoryFilter);
        }

        // Sort jobs
        if (sortBy === "match") {
            filtered.sort((a, b) => {
                if (a.isSponsored !== b.isSponsored) {
                    return a.isSponsored ? -1 : 1;
                }
                if (b.matchPercentage !== a.matchPercentage) {
                    return b.matchPercentage - a.matchPercentage;
                }
                return new Date(b.created_at) - new Date(a.created_at);
            });
        } else if (sortBy === "date") {
            filtered.sort((a, b) => {
                const dateDifference = new Date(b.created_at) - new Date(a.created_at);
                if (dateDifference !== 0) {
                    return dateDifference;
                }
                if (a.isSponsored !== b.isSponsored) {
                    return a.isSponsored ? -1 : 1;
                }
                return 0;
            });
        } else if (sortBy === "title") {
            filtered.sort((a, b) => {
                const titleCompare = a.title.localeCompare(b.title);
                if (titleCompare !== 0) {
                    return titleCompare;
                }
                if (a.isSponsored !== b.isSponsored) {
                    return a.isSponsored ? -1 : 1;
                }
                return 0;
            });
        }

        setFilteredMatches(filtered);
    };

    const handleCategoryClick = (category) => {
        setCategoryFilter(prev => prev === category ? '' : category);
    };

    const handleClearAll = () => {
        setCategoryFilter('');
    };

    const handleShareClick = (job, e) => {
        e.stopPropagation();
        setShareDropdown({ open: true, job });
    };

    const handleCloseShare = () => {
        setShareDropdown({ open: false, job: null });
    };

    const handleBookmark = async (jobId) => {
        if (!user) return;

        try {
            const isBookmarked = bookmarkedJobs.includes(jobId);
            
            if (isBookmarked) {
                const { error } = await supabase
                    .from('applications')
                    .delete()
                    .eq('user_id', user.id)
                    .eq('job_listing_id', jobId)
                    .eq('status', 'bookmarked');

                if (error) throw error;
                
                setBookmarkedJobs(prev => prev.filter(id => id !== jobId));
                showSuccess("Job removed from bookmarks");
            } else {
                const now = new Date().toISOString();
                const { error } = await supabase
                    .from('applications')
                    .upsert({
                        user_id: user.id,
                        job_listing_id: jobId,
                        status: 'bookmarked',
                        stage: 'Applications',
                        application_date: now,
                        last_updated: now
                    }, { onConflict: 'user_id,job_listing_id' });

                if (error) throw error;
                
                setBookmarkedJobs(prev => [...prev, jobId]);
                showSuccess("Job bookmarked successfully");
            }
        } catch (error) {
            console.error('Error toggling bookmark:', error);
            showError("Failed to update bookmark. Please try again.");
        }
    };

    const handleDismiss = async (jobId) => {
        const confirmed = window.confirm("Are you sure you want to remove this match?");
        if (!confirmed) {
            return;
        }

        try {
            // Update local UI immediately
            setFilteredMatches(prev => prev.filter(job => job.id !== jobId));
            setJobMatches(prev => prev.filter(job => job.id !== jobId));

            // Merge with any existing dismissed job matches so we stay in sync
            // with the Top Job Matches widget on the employee dashboard
            const existingDismissed = user.user_metadata?.dismissed_job_matches || dismissedJobMatches || [];
            const updatedDismissed = Array.from(new Set([...existingDismissed, jobId]));
            setDismissedJobMatches(updatedDismissed);

            const { error } = await supabase.auth.updateUser({
                data: {
                    dismissed_job_matches: updatedDismissed
                }
            });

            if (error) {
                throw error;
            }

            showSuccess("Job match removed");
        } catch (error) {
            console.error("Error dismissing job match:", error);
            showError("Failed to remove job match. Please try again.");
        }
    };

    const totalJobs = jobMatches.length;
    const displayedJobs = filteredMatches.length;
    const showingText = totalJobs > 100 ? `Showing ${displayedJobs} of 100+` : `Showing ${displayedJobs} of ${totalJobs}`;

    return (
        <>
        <style>{`
            @media (max-width: 1024px) {
                .recommended-matches-container {
                    padding: 1rem !important;
                }
                .recommended-matches-container h1 {
                    font-size: 1.5rem !important;
                    margin-bottom: 1rem !important;
                }
                .recommended-matches-filters {
                    display: none !important;
                }
                .recommended-matches-filters-mobile {
                    display: block !important;
                    margin-bottom: 1rem !important;
                }
                .recommended-matches-filters-mobile select {
                    width: 100% !important;
                    padding: 0.75rem !important;
                    font-size: 0.875rem !important;
                    border: 1px solid #e5e7eb !important;
                    border-radius: 0.5rem !important;
                }
                .recommended-matches-selected-category {
                    display: flex !important;
                    flex-wrap: wrap !important;
                    gap: 0.5rem !important;
                    margin-top: 0.75rem !important;
                }
                .recommended-matches-selected-category span {
                    font-size: 0.75rem !important;
                    padding: 0.375rem 0.75rem !important;
                }
                .recommended-matches-header {
                    flex-direction: column !important;
                    align-items: flex-start !important;
                    gap: 0.75rem !important;
                }
                .recommended-matches-sort {
                    width: 100% !important;
                }
                .recommended-matches-sort button {
                    width: 100% !important;
                    justify-content: space-between !important;
                }
                .recommended-matches-grid {
                    grid-template-columns: 1fr !important;
                    gap: 0.75rem !important;
                }
                .recommended-matches-card {
                    padding: 0.75rem !important;
                }
                .recommended-matches-card h3 {
                    font-size: 0.875rem !important;
                    line-height: 1.25rem !important;
                }
                .recommended-matches-card-content {
                    flex-direction: column !important;
                    gap: 0.75rem !important;
                    margin-top: 2rem !important;
                }
                .recommended-matches-card-actions {
                    width: 100% !important;
                    align-items: flex-start !important;
                }
                .recommended-matches-card-actions button {
                    width: 100% !important;
                    padding: 0.625rem 1rem !important;
                    font-size: 0.875rem !important;
                }
            }
        `}</style>
        <div className="w-full bg-white min-h-screen">
            <div className="max-w-7xl mx-auto p-6 recommended-matches-container">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-semibold text-[#061B16] mb-6">Recommended matches</h1>
                
                {/* Filter Tags - Same as company search page - Desktop */}
                {availableCategories.length > 0 && (
                    <div className="hidden md:flex flex-wrap gap-2 mb-6 recommended-matches-filters">
                        {availableCategories.map((category) => (
                            <FilterChip
                                key={category}
                                label={category}
                                active={categoryFilter === category}
                                onClick={() => handleCategoryClick(category)}
                            />
                        ))}
                        {categoryFilter && (
                            <button 
                                className="category-pill clear-all"
                                onClick={handleClearAll}
                            >
                                Clear All
                            </button>
                        )}
                    </div>
                )}

                {/* Category dropdown - Mobile */}
                {availableCategories.length > 0 && (
                    <div className="md:hidden mb-6 recommended-matches-filters-mobile">
                        <label className="block text-sm text-gray-700 mb-2">Browse by category</label>
                        <select
                            value={categoryFilter}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === '') {
                                    handleClearAll();
                                } else {
                                    handleCategoryClick(val);
                                }
                            }}
                            className="w-full bg-white border border-gray-200 rounded-lg py-2 px-3 text-sm"
                        >
                            <option value="">All Categories</option>
                            {availableCategories.map((category) => (
                                <option key={category} value={category}>{category}</option>
                            ))}
                        </select>
                        {categoryFilter && (
                            <div className="recommended-matches-selected-category">
                                <span className={`category-pill ${getCategoryClass(categoryFilter)} active`}>
                                    {categoryFilter}
                                </span>
                                <button 
                                    className="category-pill clear-all"
                                    onClick={handleClearAll}
                                >
                                    Clear All
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Results Summary and Sort */}
                <div className="flex items-center justify-between mb-6 recommended-matches-header">
                    <span className="text-sm text-gray-600">{showingText}</span>
                    <div className="flex items-center gap-2 relative recommended-matches-sort">
                        <button 
                            onClick={() => setShowSortDropdown(!showSortDropdown)}
                            className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-full bg-white text-sm text-gray-900 hover:bg-gray-50 transition-colors"
                        >
                            <span>Sort by</span>
                            <div className="flex flex-col">
                                <svg className="w-3 h-3 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                                <svg className="w-3 h-3 text-gray-900 -mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </button>
                        {showSortDropdown && (
                            <>
                                <div 
                                    className="fixed inset-0 z-10" 
                                    onClick={() => setShowSortDropdown(false)}
                                />
                                <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[160px]">
                                    <button
                                        onClick={() => {
                                            setSortBy("match");
                                            setShowSortDropdown(false);
                                        }}
                                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg ${
                                            sortBy === "match" ? "bg-gray-50 font-medium" : ""
                                        }`}
                                    >
                                        Match %
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSortBy("date");
                                            setShowSortDropdown(false);
                                        }}
                                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                                            sortBy === "date" ? "bg-gray-50 font-medium" : ""
                                        }`}
                                    >
                                        Date Posted
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSortBy("title");
                                            setShowSortDropdown(false);
                                        }}
                                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 last:rounded-b-lg ${
                                            sortBy === "title" ? "bg-gray-50 font-medium" : ""
                                        }`}
                                    >
                                        Job Title
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Job Cards Grid - Smaller, more compact cards */}
            {loading ? (
                <div className="text-center py-12 text-gray-600">Loading job matches...</div>
            ) : filteredMatches.length === 0 ? (
                <div className="text-center py-12 text-gray-600">No job matches found for the selected category.</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 recommended-matches-grid">
                    {filteredMatches.map((job) => (
                        <div key={job.id} className="bg-white border border-gray-200 rounded-lg p-4 relative recommended-matches-card">

                            <div className="flex items-center gap-2 flex-wrap">
                                {/* Sponsored Badge - Top Left */}
                                {job.isSponsored && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="bg-gray-900 text-white text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full">
                                            Sponsored
                                        </span>
                                    </div>
                                )}

                                {/* job match percentage badge top left */}
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="relative group">
                                        {/* <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded-full cursor-help">
                                            {job.matchPercentage}% Job Match
                                        </span> */}
                                        <div className="absolute left-0 bottom-full mt-2 z-20 hidden group-hover:block">
                                            <div className="w-56 rounded-lg border border-gray-200 bg-white shadow-md p-3">
                                                <div className="text-xs font-semibold text-gray-900 mb-1">
                                                    Matched categories
                                                </div>
                                                {Array.isArray(job.matchedCategories) && job.matchedCategories.length > 0 ? (
                                                    <ul className="list-disc pl-4 text-xs text-gray-700 space-y-0.5">
                                                        {job.matchedCategories.map((cat) => (
                                                            <li key={cat}>{cat}</li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <div className="text-xs text-gray-600">
                                                        No category-level matches yet.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            

                            {/* Action Icons - Top Right */}
                            <div className="absolute top-3 right-3 flex items-center gap-1">
                                <div className="relative">
                                    <button
                                        onClick={(e) => handleShareClick(job, e)}
                                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                                        title="Share"
                                    >
                                        <img src="/Share.svg" alt="Share" className="w-3.5 h-3.5" />
                                    </button>
                                    {shareDropdown.open && shareDropdown.job?.id === job.id && (
                                        <ShareDropdown
                                            url={`${window.location.origin}/job/${job.id}`}
                                            title={job.title}
                                            text={`${job.title} at ${job.company}${job.location ? ` • ${job.location}` : ""}`}
                                            onClose={handleCloseShare}
                                        />
                                    )}
                                </div>
                                <button
                                    onClick={() => handleBookmark(job.id)}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    title={bookmarkedJobs.includes(job.id) ? "Remove bookmark" : "Bookmark"}
                                >
                                    {bookmarkedJobs.includes(job.id) ? (
                                        <IoBookmark className="w-3.5 h-3.5 text-black" />
                                    ) : (
                                        <IoBookmarkOutline className="w-3.5 h-3.5 text-gray-600" />
                                    )}
                                </button>
                                <button
                                    onClick={() => handleDismiss(job.id)}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    title="Dismiss"
                                >
                                    <img src="/cross.svg" alt="Close" className="w-3.5 h-3.5" />
                                </button>
                            </div>

                            {/* Job Content - Horizontal Layout */}
                            <div className="mt-5 flex items-start justify-between gap-4 recommended-matches-card-content">
                                {/* Left Side - Job Info */}
                                <div className="flex-1">
                                    <h3 className="text-base font-semibold text-gray-900 mb-1">{job.title}</h3>
                                    <div className="flex items-center gap-1 text-sm text-gray-900">
                                        <span>{job.company}</span>
                                        {job.location && (
                                                <div className="flex items-center gap-1 -ml-1">
                                                    <img src="/Location.svg" alt="Location" className="w-3.5 h-3.5" />
                                                    <span>{job.location}</span>
                                                </div>
                                        )}
                                    </div>
                                </div>

                                {/* Right Side - Button and Date */}
                                <div className="flex flex-col items-end recommended-matches-card-actions">
                                    <button
                                        onClick={() => {
                                            if (job.application_url) {
                                                window.open(job.application_url, '_blank');
                                            } else {
                                                navigate(`/job/${job.id}`);
                                            }
                                        }}
                                        className="bg-black text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-gray-800 transition-colors mb-1"
                                    >
                                        View Job
                                    </button>
                                    <p className="text-xs text-gray-900">Posted {job.postedDays} days ago</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            </div>
        </div>
        </>
    );
};

export default RecommendedMatches;

